import { Upload as UploadIcon } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { Geoblock } from '@/models/Geoblock';

import { EFFECTS, GEOGRAPHY_LEVELS } from './GeoblockConstants';
import GeoblockImportModal from './GeoblockImportModal';

type Props = {
  name: string;
};

const toCsvType = (type: string) => (type === 'PostalCode' ? 'Zip/PostalCode' : type);

const GeoblockImportExport = ({ name }: Props) => {
  const { setValue, watch } = useFormContext();
  const [openImport, setOpenImport] = useState(false);

  const rules = watch(`${name}.rules`) as Geoblock['rules'] | undefined;
  const hasActiveRules = useMemo(() => Array.isArray(rules) && rules.some((r) => r.values.length > 0), [rules]);

  const handleImport = (imported: Geoblock) => {
    setValue(`${name}.rules`, imported.rules, { shouldDirty: true });
  };

  const handleDownload = () => {
    const header = 'type,effect,value';
    let csvContent: string;

    if (hasActiveRules && rules) {
      const rows = rules.flatMap((rule) => rule.values.map((v) => `${toCsvType(rule.type)},${EFFECTS[rule.effect]},${v}`));
      csvContent = [header, ...rows].join('\n');
    } else {
      const typeOptions = Object.keys(GEOGRAPHY_LEVELS).map(toCsvType).join('|');
      const effectOptions = Object.values(EFFECTS).join('|');
      csvContent = [header, `${typeOptions},${effectOptions},US|US-NY|US-NY-DMA_501|US-NY-New_York|US-NY-10001>`].join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'geoblock_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        size="small"
        onClick={() => setOpenImport(true)}
        startIcon={<UploadIcon />}
      >
        Import from CSV
      </Button>
      <Button color="primary" variant="outlined" size="small" fullWidth startIcon={<DownloadIcon />} onClick={handleDownload}>
        {hasActiveRules ? 'Export Active Rules' : 'Download Template'}
      </Button>

      {openImport && <GeoblockImportModal onClose={() => setOpenImport(false)} onImport={handleImport} />}
    </>
  );
};

export default GeoblockImportExport;
